import { Component, Input, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { finalize } from 'rxjs/operators';
import { environment } from 'src/app/configs/environment';
import { RegularExpressions } from 'src/app/core/helpers/regular-expressions/regular-expressions';
import { ImageCompression } from 'src/app/core/helpers/image-compression/image-compression';
import { ModalService } from 'src/app/core/services/modal/modal.service';
import { SupervisedApplicationService } from 'src/app/core/services/supervised-application/supervised-application.service';
import {
  SUPERVISED_APPLICATION_ALLOWED_EXTENSIONS,
  SUPERVISED_APPLICATION_ALLOWED_TYPES,
  SUPERVISED_APPLICATION_DOCUMENT_TYPES,
  SUPERVISED_APPLICATION_MAX_FILE_SIZE,
  SUPERVISED_APPLICATION_MAX_FILE_SIZE_LABEL
} from 'src/app/core/constants/supervised-application.const';
import {
  SupervisedApplicationOption,
  SupervisedApplicationRequest
} from 'src/app/core/models/supervised-application/supervised-application';

declare let dataLayer;

type Step = 'patient' | 'formula' | 'contact' | 'summary' | 'success' | 'error';

@Component({
  selector: 'app-supervised-application',
  templateUrl: './supervised-application.component.html',
  styleUrls: ['./supervised-application.component.scss']
})
export class SupervisedApplicationComponent implements OnInit {

  @Input() idModal = 'modalSupervisedApplication';

  readonly formSteps: Step[] = ['patient', 'formula', 'contact', 'summary'];
  readonly documentTypes = SUPERVISED_APPLICATION_DOCUMENT_TYPES;
  readonly maxSize = SUPERVISED_APPLICATION_MAX_FILE_SIZE_LABEL;

  step: Step = 'patient';
  form: FormGroup;
  ipsList: SupervisedApplicationOption[] = [];
  file: File = null;
  fileError = '';
  fileTouched = false;
  compressing = false;
  // Descarta el resultado de una compresión si mientras tanto el usuario quitó o cambió el archivo.
  private fileSelection = 0;
  identificationInputType = 'number';
  sending = false;
  requestId: number = null;
  errorMessage = '';

  constructor(
    private _formBuilder: FormBuilder,
    private _modal: ModalService,
    private _supervisedApplicationService: SupervisedApplicationService
  ) {
    this.form = this._formBuilder.group({
      patient: this._formBuilder.group({
        identificationType: ['CC', Validators.required],
        identification: ['', Validators.required],
        acceptTerms: [false, Validators.requiredTrue],
        acceptHabeasData: [false, Validators.requiredTrue]
      }),
      formula: this._formBuilder.group({
        hasAuthorizationCode: [null, Validators.required],
        formulaNumber: ['', [Validators.required, Validators.maxLength(50)]],
        authorizationCode: [{ value: '', disabled: true }, [Validators.required, Validators.maxLength(50)]]
      }),
      contact: this._formBuilder.group({
        ipsCode: ['', Validators.required],
        names: ['', [Validators.required, Validators.maxLength(100), Validators.pattern(RegularExpressions.LETTERS_SPACES_SPECIALS)]],
        lastNames: ['', [Validators.required, Validators.maxLength(100), Validators.pattern(RegularExpressions.LETTERS_SPACES_SPECIALS)]],
        cellphone: ['', [Validators.required, Validators.maxLength(10), Validators.pattern(RegularExpressions.ONLY_NUMBER_CELLPHONE)]],
        email: ['', [Validators.required, Validators.maxLength(150), Validators.pattern(RegularExpressions.VALIDATE_EMAIL)]],
        alternateCellphone: ['', [Validators.minLength(7), Validators.maxLength(10), Validators.pattern(RegularExpressions.ONLY_NUMBERS)]]
      })
    });
    this.setIdentificationValidators('CC');
  }

  ngOnInit(): void {
    this._supervisedApplicationService.getIps().subscribe(ips => this.ipsList = ips);
  }

  get patientGroup(): FormGroup {
    return this.form.get('patient') as FormGroup;
  }

  get formulaGroup(): FormGroup {
    return this.form.get('formula') as FormGroup;
  }

  get contactGroup(): FormGroup {
    return this.form.get('contact') as FormGroup;
  }

  get stepNumber(): number {
    return this.formSteps.indexOf(this.step) + 1;
  }

  open() {
    this.reset();
    this._modal.openModal(this.idModal);
  }

  close() {
    this._modal.closeModal(this.idModal);
    this.reset();
  }

  reset() {
    this.form.reset({
      patient: { identificationType: 'CC', identification: '', acceptTerms: false, acceptHabeasData: false },
      formula: { hasAuthorizationCode: null, formulaNumber: '', authorizationCode: '' },
      contact: { ipsCode: '', names: '', lastNames: '', cellphone: '', email: '', alternateCellphone: '' }
    });
    this.formulaGroup.get('authorizationCode').disable();
    this.setIdentificationValidators('CC');
    this.file = null;
    this.fileError = '';
    this.fileTouched = false;
    this.compressing = false;
    this.fileSelection++;
    this.sending = false;
    this.requestId = null;
    this.errorMessage = '';
    this.step = 'patient';
  }

  onIdentificationTypeChange(type: string) {
    this.patientGroup.get('identification').setValue('');
    this.setIdentificationValidators(type);
  }

  // Mismas reglas que el paso /datos-paciente del pedido; CD (Carné Diplomático) es alfanumérico.
  private setIdentificationValidators(type: string) {
    const control = this.patientGroup.get('identification');
    let maxLength = 15;
    let pattern = RegularExpressions.ONLY_NUMBERS;
    this.identificationInputType = 'number';
    if (type === 'CC' || type === 'RC') {
      maxLength = 10;
    } else if (type === 'TI') {
      maxLength = 11;
    } else if (type === 'PA' || type === 'CD') {
      pattern = RegularExpressions.ONLY_NUMBERS_AND_LETTERS;
      this.identificationInputType = 'text';
    }
    control.setValidators([Validators.required, Validators.minLength(3), Validators.maxLength(maxLength), Validators.pattern(pattern)]);
    control.updateValueAndValidity();
  }

  onHasAuthorizationCodeChange() {
    const authorizationCode = this.formulaGroup.get('authorizationCode');
    if (this.formulaGroup.get('hasAuthorizationCode').value === true) {
      authorizationCode.enable();
    } else {
      authorizationCode.reset('');
      authorizationCode.disable();
    }
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    this.setFile(input.files && input.files.length ? input.files[0] : null);
    // Permite volver a elegir el mismo archivo después de quitarlo.
    input.value = '';
  }

  setFile(file: File) {
    this.fileTouched = true;
    this.fileError = '';
    this.compressing = false;
    this.fileSelection++;
    if (!file) {
      return;
    }
    const extension = file.name.split('.').pop().toLowerCase();
    if (!SUPERVISED_APPLICATION_ALLOWED_TYPES.includes(file.type) || !SUPERVISED_APPLICATION_ALLOWED_EXTENSIONS.includes(extension)) {
      this.fileError = 'Solo se permiten archivos JPEG, JPG, PNG o PDF.';
      return;
    }
    if (file.size <= SUPERVISED_APPLICATION_MAX_FILE_SIZE) {
      this.file = file;
      return;
    }
    if (!ImageCompression.isCompressible(file)) {
      this.fileError = `El archivo supera el tamaño máximo permitido de ${this.maxSize}.`;
      return;
    }
    this.compressImage(file);
  }

  private compressImage(file: File) {
    const selection = this.fileSelection;
    this.compressing = true;
    ImageCompression.compress(file, SUPERVISED_APPLICATION_MAX_FILE_SIZE)
      .then(compressed => {
        if (selection === this.fileSelection) {
          this.file = compressed;
        }
      })
      .catch(() => {
        if (selection === this.fileSelection) {
          this.fileError = `No pudimos optimizar la imagen a ${this.maxSize}. Intenta con otra foto o un archivo más liviano.`;
        }
      })
      .then(() => {
        if (selection === this.fileSelection) {
          this.compressing = false;
        }
      });
  }

  removeFile() {
    this.file = null;
    this.fileTouched = true;
    this.compressing = false;
    this.fileSelection++;
  }

  formatSize(bytes: number): string {
    return bytes >= 1000000 ? `${(bytes / 1000000).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1000))} KB`;
  }

  isStepValid(step: Step): boolean {
    switch (step) {
      case 'patient':
        return this.patientGroup.valid;
      case 'formula':
        return this.formulaGroup.valid && this.file != null;
      case 'contact':
        return this.contactGroup.valid;
      default:
        return true;
    }
  }

  next() {
    if (this.compressing) {
      return;
    }
    if (!this.isStepValid(this.step)) {
      this.touchStep(this.step);
      return;
    }
    const index = this.formSteps.indexOf(this.step);
    this.step = this.formSteps[index + 1];
  }

  back() {
    const index = this.formSteps.indexOf(this.step);
    if (index > 0) {
      this.step = this.formSteps[index - 1];
    }
  }

  goTo(step: Step) {
    this.step = step;
  }

  private touchStep(step: Step) {
    const group = step === 'patient' ? this.patientGroup : step === 'formula' ? this.formulaGroup : this.contactGroup;
    group.markAllAsTouched();
    if (step === 'formula') {
      this.fileTouched = true;
    }
  }

  showError(control: AbstractControl): boolean {
    return control.invalid && control.touched;
  }

  documentTypeName(code: string): string {
    const type = this.documentTypes.find(item => item.code === code);
    return type ? type.name : code;
  }

  ipsName(code: string): string {
    const ips = this.ipsList.find(item => item.code === code);
    return ips ? ips.name : '';
  }

  submit() {
    if (this.sending || !this.formSteps.slice(0, 3).every(step => this.isStepValid(step))) {
      return;
    }
    const patient = this.patientGroup.value;
    const formula = this.formulaGroup.getRawValue();
    const contact = this.contactGroup.value;
    const request: SupervisedApplicationRequest = {
      identificationType: patient.identificationType,
      identification: String(patient.identification).trim(),
      acceptTerms: patient.acceptTerms,
      acceptHabeasData: patient.acceptHabeasData,
      hasAuthorizationCode: formula.hasAuthorizationCode,
      formulaNumber: formula.formulaNumber.trim(),
      authorizationCode: formula.hasAuthorizationCode ? formula.authorizationCode.trim() : null,
      ipsCode: contact.ipsCode,
      ipsName: this.ipsName(contact.ipsCode),
      names: contact.names.trim(),
      lastNames: contact.lastNames.trim(),
      cellphone: contact.cellphone,
      email: contact.email.trim(),
      alternateCellphone: contact.alternateCellphone || null,
      channel: environment.CHANNEL
    };

    this.sending = true;
    this._supervisedApplicationService.save(request, this.file)
      .pipe(finalize(() => this.sending = false))
      .subscribe(
        response => {
          this.requestId = response.id;
          this.step = 'success';
          this.tagSent();
        },
        (error: HttpErrorResponse) => {
          // El backend responde 400 con un mensaje claro cuando la validación falla.
          this.errorMessage = error.status === 400 && error.error && error.error.descripcion
            ? error.error.descripcion
            : 'No fue posible registrar tu solicitud. Por favor intenta nuevamente.';
          this.step = 'error';
        }
      );
  }

  private tagSent() {
    if (typeof dataLayer === 'undefined') {
      return;
    }
    dataLayer.push({
      'event': 'ga_event_botonservicios',
      'category': 'Home',
      'action': 'Envio-formulario',
      'label': 'Aplicacion supervisada',
      'UES': 'Medicamentos'
    });
  }
}
